import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { uploadAvatar } from '@/services/profiles.service';

export type Consumer = Database['public']['Tables']['consumers']['Row'];

export interface ConsumerUpdate {
  nome?: string;
  telefone?: string | null;
  morada?: string | null;
  codigo_postal?: string | null;
  cidade?: string | null;
  pais?: string | null;
  avatar_url?: string | null;
}

/**
 * Fetches the consumer profile by auth user id.
 * Returns null when there is no consumer row (PGRST116 — "not found"); the caller
 * uses this to tell consumers apart from org users. Throws on other errors.
 */
export async function getConsumerById(userId: string): Promise<Consumer | null> {
  const { data, error } = await supabase
    .from('consumers')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) {
    // PGRST116 = no row (not a consumer). 42P01 = undefined_table: the consumers
    // migration may not be applied yet during the deploy window — treat as not-a-consumer
    // so existing org-user logins never break.
    if (error.code === 'PGRST116' || error.code === '42P01') return null;
    throw error;
  }
  return data;
}

/**
 * Cria a conta de cliente (linha em `consumers`) para o utilizador autenticado,
 * via RPC security-definer. Usado quando o user escolhe "cliente" em /conta/escolher.
 * Lança 'already_org_member' se o user já pertencer a uma organização.
 */
export async function criarContaConsumer(): Promise<void> {
  const { error } = await supabase.rpc('criar_conta_consumer');
  // Erro cru de propósito: o chamador inspeciona .message para 'already_org_member'.
  // Os restantes casos caem na chave i18n genérica, logo nada de internals da BD chega à UI.
  if (error) throw error;
}

/**
 * Updates the authenticated consumer's own row (RLS: id = auth.uid()).
 * Stamps `atualizado_em` client-side, matching existing services.
 */
export async function updateConsumer(userId: string, patch: ConsumerUpdate): Promise<Consumer> {
  const { data, error } = await supabase
    .from('consumers')
    .update({ ...patch, atualizado_em: new Date().toISOString() })
    .eq('id', userId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

const AVATAR_BUCKET = 'avatars';

/**
 * Faz upload da foto do consumer para o bucket `avatars` (pasta = auth.uid())
 * e devolve o URL público. Reutiliza o uploader genérico de profiles.service.
 */
export async function uploadConsumerAvatar(consumerId: string, file: File): Promise<string> {
  return uploadAvatar({ userId: consumerId, file });
}

/**
 * Grava (ou limpa, com null) o avatar do consumer via RPC security-definer,
 * que também propaga para os leads desnormalizados (clientes_potenciais).
 */
export async function setConsumerAvatarUrl(avatarUrl: string | null): Promise<void> {
  const { error } = await supabase.rpc('definir_avatar_consumer', { p_avatar_url: avatarUrl });
  if (error) throw error;
}

/**
 * Remove a foto: apaga o objeto do storage (se o path for derivável do URL)
 * e limpa o avatar_url (RPC) — que propaga o null aos leads.
 */
export async function removeConsumerAvatar(consumerId: string, avatarUrl: string | null): Promise<void> {
  if (avatarUrl) {
    const marker = `/${AVATAR_BUCKET}/`;
    const idx = avatarUrl.indexOf(marker);
    if (idx !== -1) {
      const path = avatarUrl.slice(idx + marker.length);
      const { error } = await supabase.storage.from(AVATAR_BUCKET).remove([path]);
      if (error) throw error;
    }
  }
  await setConsumerAvatarUrl(null);
}
